import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { apiErrorMessage, createInvoiceBatch, fetchInvoiceQueue } from './api';
import { InvoiceCreationDialog } from './InvoiceCreationDialog';
import InvoiceDetailPage from './InvoiceDetailPage';
import type { InvoiceBatchPlan, InvoiceBatchRequest, InvoiceQueueRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Invoicing ▸ Invoice Queue (nav `acct-invoice-queue`).
// Port of legacy ALPACA `Samplecheckin_ListView_Invoice` (Model.xafml :45675)
// with `InvoicingViewController` :283-305 (its filter) and :1037-1216 (New).
//
// The jobs with tests still to invoice. The criteria live in
// `dbo.AI_Invoicing_Queue_SP`, not here.
//
// TOOLBAR — legacy shows New (tooltip "New Invoice") with Refresh and Export;
// Edit and Invoice History are hidden there (Model.xafml :45786-45789), and a
// row does not open on double-click (HideDetailViewController :284).
//
// NEW with ONE job ticked opens the full-page New Invoice for it directly
// (owner request 2026-10-26 — there is nothing to group). With SEVERAL ticked it
// opens the "Invoice Creation Method" popup (owner design
// ALPACA_Invoice_Creation_Prototype_v13, 2026-10-26): Batch (one invoice per
// job) · Multiple (one per client) · Group (one per client, jobs received in the
// Invoice Period). OK with ONE proposed invoice opens the full-page New Invoice
// detail for those jobs, so its prices are reviewed before saving (legacy
// "Single" mode); OK with several confirms and creates them all in one
// transaction (legacy Batch Invoicing saves without opening each one).
// Legacy's rules, in legacy's words:
//   · no rows at all            → "If there is not pending jobID."
//   · rows but none ticked      → "Please select an item to proceed further."
// The server checks the same rules again (§14b.5).
//
// AUDIT. FormName "Invoice Queue" — the nav caption legacy audits by.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Invoice Queue';

const COLUMNS: AppColumn<InvoiceQueueRow>[] = [
  { field: 'jobID',        header: 'JobID',        width: 120 },
  { field: 'client',       header: 'Client',       width: 200 },
  { field: 'projectID',    header: 'ProjectID',    width: 120 },
  { field: 'projectName',  header: 'ProjectName',  width: 190 },
  { field: 'quoteID',      header: 'QuoteID',      width: 120 },
  { field: 'dueDate',      header: 'DueDate',      width: 110, type: 'date' },
  { field: 'creditRating', header: 'CreditRating', width: 110 },
  { field: 'receivedDate', header: 'ReceivedDate', width: 150, type: 'datetime' },
  { field: 'status',       header: 'Status',       width: 140, type: 'status' },
];

type Screen = { kind: 'list' } | { kind: 'detail'; jobIds: string[]; key: number };

export default function InvoiceQueuePage() {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const perm   = useNavActions('acct-invoice-queue');
  const [rows, setRows]       = useState<InvoiceQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [screen, setScreen]   = useState<Screen>({ kind: 'list' });
  const [methodFor, setMethodFor] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchInvoiceQueue());
      // A reload can drop rows (they were just invoiced); a surviving
      // selection would point at nothing.
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the Invoice Queue.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // The Search box narrows the rows already fetched.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => COLUMNS.some((c) => {
      const v = r[c.field as keyof InvoiceQueueRow];
      return typeof v === 'string' && v.toLowerCase().includes(q);
    }));
  }, [rows, search]);

  /** Legacy NewObjectAction_Executing (:1037-1216). */
  function newInvoice() {
    if (rows.length === 0) { notify.warning('If there is not pending jobID.'); return; }
    const ids = [...selected].map(String);
    if (ids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    // One job: nothing to group, so straight to the New Invoice.
    if (ids.length === 1) { setScreen({ kind: 'detail', jobIds: ids, key: Date.now() }); return; }
    setMethodFor(ids);
  }

  /** Popup OK — one invoice opens the detail for review; several are created. */
  async function createFromMethod(request: InvoiceBatchRequest, plan: InvoiceBatchPlan) {
    const only = plan.invoices.length === 1 ? plan.invoices[0] : undefined;
    if (only) {
      setMethodFor(null);
      setScreen({ kind: 'detail', jobIds: only.jobs.map((j) => j.oid), key: Date.now() });
      return;
    }
    const ok = await confirm({
      title: 'Create invoices',
      message: `Do you want to create ${plan.invoices.length} invoices for ${plan.eligibleJobCount} Job IDs?`,
    });
    if (!ok) return;
    try {
      const created = await createInvoiceBatch(request);
      setMethodFor(null);
      notify.success(`${created.length} invoices created successfully.`);
      const noPdf = created.filter((c) => !c.pdfStored).length;
      if (noPdf > 0) notify.warning(`${noPdf} of the invoices were saved, but their PDF could not be generated.`);
      void load();
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'The invoices could not be created.');
    }
  }

  if (screen.kind === 'detail') {
    return (
      <InvoiceDetailPage
        key={screen.key}
        jobIds={screen.jobIds}
        onClose={() => setScreen({ kind: 'list' })}
        onSaved={() => { setScreen({ kind: 'list' }); void load(); }}
      />
    );
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="acct-invoice-queue" loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />
      <AppToolbar
        // MAC: New needs the Create right — hidden (not greyed) for a Read-only role.
        onNew={perm.create ? newInvoice : undefined}
        onRefresh={() => void load()}
        refreshing={loading}
        exportData={{ filename: 'invoice-queue', ...gridExportMatrix(shown, COLUMNS) }}
      />
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<InvoiceQueueRow>
        rows={shown}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        emptyText={search ? 'No results match your search.' : 'No jobs are waiting to be invoiced.'}
      />
      <InvoiceCreationDialog
        open={methodFor !== null}
        jobIds={methodFor ?? []}
        onClose={() => setMethodFor(null)}
        onConfirm={(request, plan) => void createFromMethod(request, plan)}
      />
    </div>
  );
}
